import React from 'react';
import { Drawer, Form, Button, SelectPicker, CheckPicker, Message } from 'rsuite';
import { useTranslation } from 'react-i18next';

const daysOfWeek = [
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
  { label: 'Sunday', value: 7 }
];

const DrawerView = ({
  open,
  onClose,
  onFiltersChange,
  onResetFilters,
  currentFilters,
  zones,
  withPriority,
  toaster
}) => {
  const [formValue, setFormValue] = React.useState(currentFilters);

  // Define initial filter state based on withPriority
  const getInitialFilters = () => {
    if (withPriority) {
      return {
        name: ''
      };
    }
    return {
      name: '',
      zones: []
    };
  };

  // Transform zones data for SelectPicker
  const zoneOptions = React.useMemo(() => {
    return (
      zones?.map(zone => ({
        label: zone.name,
        value: zone._id
      })) || []
    );
  }, [zones]);

  const handleSubmit = () => {
    const hasFilters = Object.values(formValue).some(value => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '' && value !== null;
    });

    if (hasFilters) {
      onFiltersChange(formValue);
      toaster.push(
        <Message type="success" duration={3000}>
          {t('Filters applied successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );
    } else {
      onResetFilters();
    }

    onClose();
  };

  const handleReset = () => {
    const initialFilters = getInitialFilters();
    setFormValue(initialFilters);
    onResetFilters();
    toaster.push(
      <Message type="info" duration={3000}>
        Filters have been reset!
      </Message>,
      { placement: 'topCenter' }
    );

    onClose();
  };

  React.useEffect(() => {
    if (open) {
      setFormValue(currentFilters || getInitialFilters());
    }
  }, [open, currentFilters]);
  const { t } = useTranslation();
  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Filter Options')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleReset} appearance="subtle">
            {t('Reset')}
          </Button>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Apply')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('Name')}</Form.ControlLabel>
            <Form.Control name="name" placeholder="Filter by name" block />
          </Form.Group>
          {withPriority && (
            <>
              <Form.Group>
                <Form.ControlLabel>Zone</Form.ControlLabel>
                <Form.Control
                  name="zone"
                  accepter={SelectPicker}
                  data={zoneOptions}
                  searchable
                  placeholder={t('Select a zone')}
                  block
                  onChange={value =>
                    setFormValue(prev => ({ ...prev, zone: value, priority: '', dayOfWeek: '' }))
                  }
                />
              </Form.Group>

              {formValue.zone && (
                <>
                  <Form.Group>
                    <Form.ControlLabel>{t('Day of Week')}</Form.ControlLabel>
                    <Form.Control
                      name="dayOfWeek"
                      accepter={SelectPicker}
                      data={daysOfWeek}
                      searchable
                      placement="auto"
                      placeholder={t('Select day of week')}
                      block
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.ControlLabel>{t('Priority')}</Form.ControlLabel>
                    <Form.Control
                      name="priority"
                      type="number"
                      placeholder="Enter priority"
                      block
                    />
                  </Form.Group>
                </>
              )}
            </>
          )}

          {!withPriority && (
            <Form.Group>
              <Form.ControlLabel>Zones</Form.ControlLabel>
              <Form.Control
                name="zones"
                accepter={CheckPicker}
                data={zoneOptions}
                searchable
                placeholder={t('Select zones')}
                block
                sticky
                cleanable
              />
            </Form.Group>
          )}
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default DrawerView;
